import { fetchConfiguration } from './api-clients';
import { EventChunkUploader } from './event-chunk-uploader';
import { ErrorNotifier } from './error-notifier';
import { EventLog } from './event-log';
import { MessageManager } from './message-manager';
import { MessagePersistence } from './messages/message-persistence';
import { Tracker, TrackerSetupOptions } from './tracker.types';
import { Idfv } from './idfv';
import { migrateIdfv } from './migrate-idfv';
import { PushNotificationSubscriber } from './push-notification-subscriber';
import { SpaMode } from './spamode';

const previewOptions = {
  spa_mode: SpaMode.NONE as SpaMode,
  cookie_domain: undefined as string | undefined,
};

export class PreviewModeTracker implements Tracker {
  private previewToken: string;
  private idfv: string;
  private userAnnotation: string;
  private _messageManager: MessageManager;
  // @ts-ignore TS6133
  private settings: object;
  private eventChunkUploader: EventChunkUploader;
  private options: typeof previewOptions = previewOptions;
  private spaMode: SpaMode = SpaMode.NONE;
  private _pushNotificationSubscriber: PushNotificationSubscriber | null = null;
  private configured: boolean;

  constructor(previewToken: string) {
    this.idfv = '';
    this.userAnnotation = '';
    this.previewToken = previewToken;
    this.settings = {};
    this._messageManager = new MessageManager();
    this.eventChunkUploader = new EventChunkUploader();
    this.configured = false;
    MessagePersistence.disableStorage();
  }

  public async setup(token: string, options: TrackerSetupOptions): Promise<void | boolean> {
    if (!token) {
      console.error('Aborting setup process: No token set.');
      return false;
    }

    if (this.configured) {
      console.error(
        'Ignore the setup due to the preview tracker already being configured. Please check your Repro tag to avoid multiple initializations.'
      );
      return false;
    }

    this.options = {
      ...this.options,
      ...options,
    };

    this.spaMode = this.options.spa_mode;

    const idfv = new Idfv();
    this.idfv = idfv.value;

    if (!idfv.isValid()) {
      const oldIdfv = idfv;
      const newIdfv = idfv.recover();
      migrateIdfv(token, idfv.value, newIdfv.value, {
        intervalDaysToStorageExpiration: 365,
        cookieDomain: this.options.cookie_domain,
      });
      this.idfv = newIdfv.value;

      ErrorNotifier.notify({
        error: new Error('Broken idfv detected'),
        extraPayload: { oldIdfv: oldIdfv.value, newIdfv: newIdfv.value, token },
        samplingRatio: 10000,
      });
    }

    const payload = {
      userAnnotation: this.userAnnotation,
      idfv: this.idfv,
      isFirstSession: false,
      token,
      previewToken: this.previewToken,
    };

    fetchConfiguration(payload)
      .then((configuration) => {
        ErrorNotifier.init(configuration.send_errors, {
          appId: configuration.appId,
          userAnnotation: this.userAnnotation,
          idfv: this.idfv,
          isFirstSession: false,
        });

        this._messageManager.activate({
          eventChunkUploader: this.eventChunkUploader,
          eventLog: new EventLog({
            userIdentifier: '',
            isWriteAllowed: false,
          }),
        });

        if (configuration.pushSetting) {
          this._pushNotificationSubscriber = new PushNotificationSubscriber({
            onPushClick: () => {},
            onPushDataFetched: () => {},
            onPushShow: () => {},
            onSubscribeSucceed: () => {},
          });
          this._pushNotificationSubscriber.setRegistrationOptions(configuration.pushSetting);
        }

        return this._messageManager.buildMessages({
          messages: configuration.messages,
          silverEggSetting: configuration.silverEggSetting,
        });
      })
      .then(() => {
        this.configured = true;
        this._messageManager.displayPreviewMessage();
      })
      .catch((error) => {
        console.error(error);
        ErrorNotifier.notify({
          error,
          extraPayload: { ...payload, ...this.eventChunkUploader.toJSON },
        });
      });
  }

  public setSnippetVersion(): void {}
  public getDeviceID(): void {}
  public isOptedIn(): void {}
  public optIn(): void {}
  public optOut(): void {}
  public track(): void {}
  public trackViewContent(): void {}
  public trackSearch(): void {}
  public trackAddToCart(): void {}
  public trackAddToWishlist(): void {}
  public trackInitiateCheckout(): void {}
  public trackAddPaymentInfo(): void {}
  public trackPurchase(): void {}
  public trackShare(): void {}
  public trackLead(): void {}
  public trackCompleteRegistration(): void {}
  public setUserID(): void {}
  public setStringUserProfile(): void {}
  public setIntUserProfile(): void {}
  public setDoubleUserProfile(): void {}
  public setDateUserProfile(): void {}
  public setUserGender(): void {}
  public setUserEmailAddress(): void {}
  public setUserResidencePrefecture(): void {}
  public setUserDateOfBirth(): void {}
  public setUserAge(): void {}
  public updateLinkerParameters(): void {}
  /* eslint no-empty-function:1 */
  public async _handlePageChange(pagechangeMode: SpaMode): Promise<void> {
    console.warn(
      'pagechange has been detected on in-site message preview mode. Please reload without preview token for normal operation.'
    );
    if (this.spaMode === pagechangeMode) {
      this._messageManager.handlePagechange();
    } else if (typeof history.pushState === 'function') {
      this._messageManager.hideVisibleMessages();
    }
  }

  public _generateLinkerParameters(): string {
    return '';
  }

  public subscribeToPushNotification(): void {
    console.warn(
      '"subscribeToPushNotification" can\'t be called while using in-site message preview. Please deliver the desired message using Tester and check how the message works.'
    );
  }

  public unsubscribeFromPushNotification(): void {
    console.warn(
      '"unsubscribeFromPushNotification" can\'t be called while using in-site message preview. Please deliver the desired message using Tester and check how the message works.'
    );
  }

  public isSubscribedToPushNotification(): Promise<boolean> {
    console.warn(
      '"isSubscribedToPushNotification" can\'t be called while using in-site message preview. Please deliver the desired message using Tester and check how the message works.'
    );
    return Promise.resolve(false);
  }

  public isPushNotificationSubscribeAllowed(): boolean {
    if (!this._pushNotificationSubscriber) return false;
    return this._pushNotificationSubscriber.isSubscribeAllowed;
  }

  public on() {
    console.warn('registering event hook has been ignored while using in-site message preview.');
  }

  public event() {
    this.on();
  }

  public off() {
    console.warn('deregistering event hook has been ignored while using in-site message preview.');
  }

  public _log(): void {}
}
